import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, Input, OnInit } from '@angular/core';
import { FontAwesomeIcon } from '@core/enums/font-awesome-icon.enum';
import { ToastService } from '@capturum/ui/api';
import { TranslateService } from '@ngx-translate/core';
import { RenderItem, XmlNode } from './xml-viewer.model';

@Component({
  selector: 'app-xml-viewer',
  templateUrl: './xml-viewer.component.html',
  styleUrls: ['./xml-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class XmlViewerComponent implements OnInit {
  @Input() public xml: string = '';
  
  public formattedXml: string = '';
  public xmlTree: XmlNode[] = [];
  public renderItems: RenderItem[] = [];
  public loading: boolean = false;
  public error: string = '';
  public readonly FontAwesomeIcon = FontAwesomeIcon;

  constructor(
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService
  ) {}

  public ngOnInit(): void {
    this.parseXml();
  }

  private parseXml(): void {
    try {
      if (!this.xml) {
        this.error = this.translateService.instant('intergrip.student-fetch-service.detail.xml.no-data');
        return;
      }

      // Clear any previous errors
      this.error = '';
      this.formattedXml = this.xml;
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(this.xml, 'text/xml');
      
      if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
        throw new Error(this.translateService.instant('intergrip.student-fetch-service.detail.xml.failed-parse-data'));
      }

      this.xmlTree = this.parseNode(xmlDoc.documentElement, 0);
      this.updateRenderItems();
    } catch (e) {
      this.error = this.translateService.instant('intergrip.student-fetch-service.detail.xml.failed-parse-data');
      console.error(e);
    }
  }

  private updateRenderItems(): void {
    this.renderItems = this.calculateRenderItems();
  }

  private parseNode(node: Node, level: number, parentId: string = ''): XmlNode[] {
    const nodes: XmlNode[] = [];

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const tagName = element.tagName;
      const nodeId = `${parentId}-${tagName}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Check if this is a collapsible node (Leerling)
      const isCollapsible = tagName === 'Leerling';
      
      // Check if element is empty or has only whitespace
      const textContent = element.textContent?.trim() || '';
      const hasChildren = element.children.length > 0;
      const isEmpty = !textContent && !hasChildren;

      // Parse child nodes first to determine structure
      const childNodes: XmlNode[] = [];
      for (let i = 0; i < node.childNodes.length; i++) {
        const childNode = node.childNodes[i];
        
        if (childNode.nodeType === Node.TEXT_NODE) {
          const text = childNode.textContent?.trim();
          if (text) {
            childNodes.push({
              type: 'text',
              text,
              level: level + 1
            });
          }
        } else if (childNode.nodeType === Node.ELEMENT_NODE) {
          childNodes.push(...this.parseNode(childNode, level + 1, nodeId));
        }
      }

      const hasElementChildren = childNodes.some(c => this.isElementNode(c));

      const xmlNode: XmlNode = {
        type: 'element',
        name: tagName,
        level,
        isCollapsible,
        isExpanded: true,
        id: nodeId,
        isEmpty,
        hasElementChildren,
        children: childNodes
      };

      nodes.push(xmlNode);
    }

    return nodes;
  }

  public toggleNode(node: XmlNode): void {
    if (node.isCollapsible) {
      node.isExpanded = !node.isExpanded;
      this.updateRenderItems();
      this.cdr.markForCheck();
    }
  }

  private isElementNode(node: XmlNode): boolean {
    return node.type === 'element';
  }

  private isTextNode(node: XmlNode): boolean {
    return node.type === 'text';
  }

  private calculateRenderItems(nodes: XmlNode[] = this.xmlTree): RenderItem[] {
    const result: RenderItem[] = [];
    let idCounter = 0;
    
    const flatten = (nodeList: XmlNode[], visible: boolean, parentExpanded: boolean): void => {
      for (const node of nodeList) {
        const nodeVisible = visible && parentExpanded;
        
        if (this.isElementNode(node)) {
          const isExpanded = node.isExpanded !== false;
          
          result.push({ 
            type: 'opening', 
            node, 
            visible: nodeVisible,
            level: node.level,
            id: `${node.id}-open-${idCounter++}`
          });
          
          if (isExpanded && node.children && node.children.length > 0) {
            flatten(node.children, nodeVisible, isExpanded);
          }
          
          const hasElementChildren = node.children && node.children.some(c => this.isElementNode(c));
          if (hasElementChildren || (node.children && node.children.length > 1)) {
            result.push({ 
              type: 'closing', 
              node, 
              visible: nodeVisible && isExpanded,
              level: node.level,
              id: `${node.id}-close-${idCounter++}`
            });
          }
        } else if (this.isTextNode(node)) {
          result.push({ 
            type: 'text', 
            node, 
            visible: nodeVisible,
            level: node.level,
            id: `text-${idCounter++}`
          });
        }
      }
    };
    
    flatten(nodes, true, true);
    return result;
  }

  public getIndentation(level: number): string {
    return '  '.repeat(level);
  }

  public getValueClass(text: string | undefined): string {
    if (!text || text.trim() === '' || text.toLowerCase() === 'null') {
      return 'xml-empty';
    }
    return 'xml-text';
  }

  @HostListener('keydown.control.a', ['$event'])
  public handleCtrlA(event: KeyboardEvent): void {
    event.preventDefault();
    this.selectAll();
  }

  public copyToClipboard(): void {
    if (navigator.clipboard && this.formattedXml) {
      navigator.clipboard.writeText(this.formattedXml).then(() => {
          this.toastService.success(this.translateService.instant('intergrip.student-fetch-service.detail.xml.copy-to-clipboard-success'), this.translateService.instant('intergrip.student-fetch-service.detail.xml.copy-to-clipboard-success'));
      }).catch(() => {
        this.toastService.error(this.translateService.instant('intergrip.student-fetch-service.detail.xml.copy-to-clipboard-error'), this.translateService.instant('intergrip.student-fetch-service.detail.xml.copy-to-clipboard-error'));
      });
    }
  }

  private selectAll(): void {
    const selection = window.getSelection();
    const contentElement = document.querySelector('.xml-viewer__content');
    
    if (!selection || !contentElement) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(contentElement);
    selection.removeAllRanges();
    selection.addRange(range);
    
    this.copyToClipboard();
  }

  public collapseAll(): void {
    this.setAllExpanded(false);
    this.updateRenderItems();
    this.cdr.markForCheck();
  }

  public expandAll(): void {
    this.setAllExpanded(true);
    this.updateRenderItems();
    this.cdr.markForCheck();
  }

  private setAllExpanded(expanded: boolean): void {
    const setExpanded = (nodes: XmlNode[]): void => {
      for (const node of nodes) {
        if (node.isCollapsible) {
          node.isExpanded = expanded;
        }
        if (node.children) {
          setExpanded(node.children);
        }
      }
    };
    setExpanded(this.xmlTree);
  }

  public trackByItemId(index: number, item: RenderItem): string {
    return item.id;
  }
}

